import {useCallback} from 'react';
import {addToast} from '@albert-io/atomic';

import {normalizeError} from 'lib/normalizeError';

import {useDeleteDiagnosticMutation} from './diagnostics.queries.generated';

/**
 * Deletes an assigned diagnostic on the acting teacher's behalf, reporting the outcome as a toast
 * and returning whether it succeeded so a caller can navigate away only on success.
 *
 * What the delete reaches depends on the teacher's authority, which mandark decides: a teacher who
 * manages every recipient removes the diagnostic outright, one who manages only some removes just
 * their own recipients and it survives for the other teachers. Nothing on `DeleteDiagnosticResult`
 * distinguishes the two, so the toast copy has to hold for both.
 */
export const useDeleteDiagnostic = () => {
  const [deleteDiagnostic] = useDeleteDiagnosticMutation();

  return useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteDiagnostic({
          variables: {id},
          update: (cache, {data}) => {
            if (!data || data.deleteDiagnostic.errors.length) return;
            cache.evict({id: cache.identify({__typename: 'Diagnostic', id})});
            cache.gc();
          }
        });
        const payload = result.data?.deleteDiagnostic;
        const errors = payload?.errors ?? [];
        // Absent data is a failure, not a silent success: `errorPolicy: 'all'` or an `onError`
        // option would resolve the promise with no payload rather than rejecting.
        if (!payload || errors.length > 0) {
          addToast({
            color: 'negative',
            title: 'Could not delete diagnostic',
            message: errors[0]?.message ?? 'Something went wrong.'
          });
          return false;
        }
        addToast({color: 'positive', title: 'Diagnostic deleted'});
        return true;
      } catch (err) {
        addToast({
          color: 'negative',
          title: 'Could not delete diagnostic',
          message: normalizeError(err).message
        });
        return false;
      }
    },
    [deleteDiagnostic]
  );
};
